import React from 'react';
import { Accordion } from '@leagueplatform/web-common-components';
import {
  ProgramBanner,
  ProgramRichText,
} from '@leagueplatform/health-journey-common';
import {
  Box,
  HeadingText,
  StackLayout,
  UtilityText,
  styled,
} from '@leagueplatform/genesis-core';
import { RewardProgramsProgress } from '#components/reward-programs-progress/reward-programs-progress.component.tsx';
import {
  NestedCampaign,
  USER_HEALTH_CAMPAIGN_STATUS,
} from '@leagueplatform/health-journey-api';
import { ActivityList } from '#components/activity-list/activity-list.component.tsx';
import { ProgramDate } from '#components/program-date/program-date.component.tsx';
import { handleStaticAsset } from '@leagueplatform/asset-config';
import { track } from '@leagueplatform/analytics';
import { REWARDS_PROGRAMS_ASSET_KEYS } from '../../asset-map';

const StyledAccordionTrigger = styled(Accordion.Trigger, {
  '&, > span': {
    padding: '$oneAndHalf',
    background: '$surfaceBackgroundSecondary',
    borderRadius: '$large',
    borderWidth: '$thin',
    borderStyle: 'solid',
    borderColor: '$onSurfaceBorderSubdued',
    color: '$onSurfaceTextPrimary',
    '> span': {
      flexGrow: 1,
    },
  },
  display: 'flex',
  width: '100%',
  position: 'relative',
  cursor: 'pointer',
  textAlign: 'start',
  '.accordion-chevron': {
    transition: 'transform 0.25s ease',
  },
  '&[aria-expanded="true"] .accordion-chevron': {
    transform: 'rotate(180deg)',
  },
  '&[aria-expanded="true"]': {
    '&, > span': {
      borderBottom: 'none',
      borderEndStartRadius: 0,
      borderEndEndRadius: 0,
    },
  },
  '&:hover': {
    '&, > span': {
      backgroundColor: '$surfaceBackgroundSecondary',
    },
  },
});

const StyledAccordionContent = styled(Accordion.Content, {
  backgroundColor: '$surfaceBackgroundPrimary',
  borderEndStartRadius: '$large',
  borderEndEndRadius: '$large',
  borderWidth: '$thin',
  borderStyle: 'solid',
  borderColor: '$onSurfaceBorderSubdued',
  borderTop: 'none',
  marginX: 1,
});

interface RewardProgramGroupProps {
  relatedCampaign: NestedCampaign;
  showDate?: boolean;
}

export const RewardProgramGroup = ({
  relatedCampaign,
  showDate,
}: RewardProgramGroupProps) => {
  const campaign = relatedCampaign.campaign.data;

  const { status, completedComponentAttributes, campaignProgress } =
    relatedCampaign;

  const { richTextDescription, name, startDate, endDate } = campaign;

  const showDescription = status !== USER_HEALTH_CAMPAIGN_STATUS.COMPLETED;

  const showCompletionBanner =
    status === USER_HEALTH_CAMPAIGN_STATUS.COMPLETED &&
    completedComponentAttributes;

  // const showEndedBanner =
  //   status === USER_HEALTH_CAMPAIGN_STATUS.ENDED && endedComponentAttributes;

  const showProgramDate =
    status === USER_HEALTH_CAMPAIGN_STATUS.ACTIVE && showDate;

  const handleAccordionChange = () => {
    track('program.button_clicked.health_campaign_detail.expand', {
      health_campaign_id: relatedCampaign.campaignId || '',
      health_campaign_name: campaign.name || '',
      health_campaign_status: relatedCampaign.status || '',
    });
  };

  return (
    <Accordion.Root collapsible type="single" onClick={handleAccordionChange}>
      <Accordion.Item value={name}>
        <Accordion.Header level="3" size="md">
          <StyledAccordionTrigger>
            <StackLayout spacing="$half">
              <HeadingText
                level="display"
                size="md"
                css={{ typography: '$headingThree' }}
              >
                {name}
              </HeadingText>
              {showDescription && (
                <UtilityText css={{ typography: '$bodyTwo', p: { margin: 0 } }}>
                  <ProgramRichText richText={richTextDescription} />
                </UtilityText>
              )}
              {showCompletionBanner && (
                <ProgramBanner
                  boldedText={{
                    iconProps: {
                      icon: handleStaticAsset(
                        REWARDS_PROGRAMS_ASSET_KEYS.REWARDS_PROGRAM_CHECK_MARK,
                      ) as string,
                      tint: '$successIcon',
                      size: 16,
                    },
                    text: (
                      <HeadingText
                        level="3"
                        size="md"
                        css={{
                          color: '$onSurfaceTextSuccess',
                          fontWeight: 600,
                        }}
                      >
                        {completedComponentAttributes.componentAttributes.title}
                      </HeadingText>
                    ),
                  }}
                  basicText={
                    <ProgramRichText
                      richText={
                        completedComponentAttributes.componentAttributes
                          ?.richText
                      }
                    />
                  }
                  css={{
                    width: 'calc(100% + $one)',
                    backgroundColor: '$successBackgroundSecondary',
                    borderRadius: '$medium',
                    padding: '$one',
                    textAlign: 'center',
                    typography: '$caption',
                  }}
                  horizontalAlignment="center"
                />
              )}
              {/* {showEndedBanner && (
              <ProgramBanner
                boldedText={{
                  iconProps: {
                    icon: endedComponentAttributes.componentAttributes?.iconUrl,
                    size: 14,
                    css: { filter: `saturate(0)` },
                  },
                  text: (
                    <HeadingText
                      level="display"
                      size="md"
                      css={{
                        typography: '$headingFour',
                      }}
                    >
                      {endedComponentAttributes.componentAttributes.title}
                    </HeadingText>
                  ),
                }}
                basicText={
                  <ProgramRichText
                    richText={
                      endedComponentAttributes.componentAttributes?.richText
                    }
                  />
                }
                css={{
                  width: '100%',
                  backgroundColor: '$successBackgroundSecondary',
                  borderRadius: '$medium',
                  padding: '$one',
                  textAlign: 'center',
                  typography: '$caption',
                }}
                horizontalAlignment="center"
              />
            )} */}

              <Box css={{ typography: '$bodyTwo', marginTop: '$half' }}>
                <RewardProgramsProgress
                  iconUrl={campaignProgress.iconUrl}
                  currentValue={campaignProgress.current}
                  maxValue={campaignProgress.threshold}
                  showProgress={false}
                />
              </Box>
              {showProgramDate && (
                <ProgramDate startDate={startDate} endDate={endDate} />
              )}
            </StackLayout>
          </StyledAccordionTrigger>
        </Accordion.Header>
        <StyledAccordionContent css={{ marginTop: '$minusQuarter' }}>
          <ActivityList compact relatedCampaign={relatedCampaign} />
        </StyledAccordionContent>
      </Accordion.Item>
    </Accordion.Root>
  );
};
